import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePaginationParams } from 'hooks/use-pagination-params';
import { useDebounce } from 'hooks/use-debounce';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from 'modules/auth/permissions';
import { QuestionSummary } from 'modules/questionBank/components/QuestionSummary';
import { AlertCircle, Library, MoreVertical, Pencil, Plus, Search, Sparkles, Trash2, Upload } from 'lucide-react';
import { GenerateWithAIModal } from 'components/questionBank/GenerateWithAIModal';
import { ImportQuestionsModal } from 'components/questionBank/ImportQuestionsModal';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import EmptyState from 'components/ui/EmptyState';
import { FilterButton, FilterReset, type FilterOption } from 'components/ui/filter-button';
import { TablePager } from 'components/ui/pagination';
import Skeleton from 'components/ui/skeleton';
import { StatusBadge } from 'components/ui/status-badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { TableCount, TableSearch } from 'components/ui/data-table';
import { PageHeader } from 'components/ui/page-header';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'components/ui/dropdown-menu';
import { cn } from 'lib/utils';
import { formatGroupedNumber } from 'lib/dateFormat';
import type { Subject } from 'modules/subject/types';
import type { BankQuestion } from 'modules/questionBank/types';
import { usePermission } from 'modules/auth/hooks';
import { useSubjectList } from 'modules/subject';
import {
  useBankQuestionList,
  useDeleteBankQuestion,
  useBulkDeleteBankQuestions,
} from 'modules/questionBank';
import { useIsMobile } from 'hooks/use-media-query';
import { QuestionBankMobile } from 'components/mobile/screens/QuestionBankMobile';
import {
  QUESTION_TYPE_META,
  QUESTION_TYPE_ORDER,
  DEFAULT_TYPE_META,
  DIFFICULTIES,
  DIFFICULTY_TONE,
} from './questionTypeMeta';
import { QuestionPreviewPanel } from './QuestionPreviewPanel';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/** The list state the bank keeps in the URL, so a filtered view can be linked and survives a refresh. */
type ListParam = 'subjectId' | 'type' | 'difficulty' | 'search';

const LoadingRows: React.FC = () => (
  <div className="divide-y divide-border" aria-hidden>
    {[0, 1, 2, 3, 4, 5].map((row) => (
      <div key={row} className="flex items-center gap-4 px-4 py-3.5">
        <Skeleton className="h-7 w-7" />
        <Skeleton className={cn('h-3.5', row % 2 ? 'w-2/5' : 'w-1/2')} />
        <span className="flex-1" />
        <Skeleton className="hidden h-3.5 w-24 md:block" />
        <Skeleton className="h-5 w-14" />
      </div>
    ))}
  </div>
);

const QuestionBankDesktop: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const subjectId = searchParams.get('subjectId') ?? undefined;
  const difficulty = DIFFICULTIES.find((d) => d === searchParams.get('difficulty'));
  const type = searchParams.get('type') ?? undefined;
  const urlSearch = searchParams.get('search') ?? '';
  const { page, limit, setPage: rawSetPage, setPageSize: rawSetPageSize } = usePaginationParams({
    defaultPage: 1,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    pageSizeParamKey: 'limit',
  });

  const { data: subjects = [], isLoading: subjectsLoading } = useSubjectList();
  const {
    data: result,
    isLoading,
    isError,
    isPlaceholderData,
    refetch,
  } = useBankQuestionList(
    { subjectId, difficulty, type, search: urlSearch.trim() || undefined, page, limit },
    { keepPrevious: true },
  );
  const deleteQuestion = useDeleteBankQuestion();
  const bulkDeleteQuestions = useBulkDeleteBankQuestions();

  const canCreate = usePermission(PERMISSIONS.QUESTIONS_CREATE);
  const canEdit = usePermission(PERMISSIONS.QUESTIONS_EDIT);
  const canDelete = usePermission(PERMISSIONS.QUESTIONS_DELETE);

  const [deleteTarget, setDeleteTarget] = useState<BankQuestion | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Bulk selection is scoped to the current page; it resets whenever the
  // filter/pagination params change so we never delete rows that aren't visible.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  // Preview is independent of bulk selection and leaves the list in place.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewTriggerRef = useRef<HTMLElement | null>(null);

  const openPreview = (id: string, trigger: HTMLElement) => {
    previewTriggerRef.current = trigger;
    trigger.focus();
    setPreviewId(id);
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Filter changes replace the history entry, so Back leaves the bank instead
  // of stepping through every filter; each change starts again at page 1.
  const updateParams = useCallback(
    (patch: Partial<Record<ListParam, string | undefined>>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
          next.delete('page');
          return next;
        },
        { replace: true },
      );
      setSelectedIds(new Set());
    },
    [setSearchParams],
  );

  // The box updates as you type; the URL (and so the query) follows once typing pauses.
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const appliedSearch = useRef(urlSearch);
  useEffect(() => {
    // Back/forward changed the URL under the box.
    if (urlSearch === appliedSearch.current) return;
    appliedSearch.current = urlSearch;
    setSearchInput(urlSearch);
  }, [urlSearch]);
  useEffect(() => {
    if (debouncedSearch === appliedSearch.current) return;
    appliedSearch.current = debouncedSearch;
    updateParams({ search: debouncedSearch || undefined });
  }, [debouncedSearch, updateParams]);

  const setPage = (p: number) => {
    rawSetPage(p);
    clearSelection();
  };

  const setPageSize = (size: number) => {
    rawSetPageSize(size);
    clearSelection();
  };

  const hasFilters = Boolean(subjectId || type || difficulty);
  const isFiltering = hasFilters || Boolean(urlSearch.trim());
  // The header counts the whole bank; the filter line counts what the filters match.
  const { data: bankCount } = useBankQuestionList({ page: 1, limit: 1 }, { enabled: isFiltering });

  const resetFilters = () => {
    setSearchInput('');
    appliedSearch.current = '';
    updateParams({ subjectId: undefined, type: undefined, difficulty: undefined, search: undefined });
  };

  // Open the full-page editor. If a subject filter is active, carry it over
  // so the new question form picks the same subject by default.
  const openCreate = () => {
    const qs = subjectId ? `?subjectId=${encodeURIComponent(subjectId)}` : '';
    navigate(`/app/questions/new${qs}`);
  };

  const openEdit = (q: BankQuestion) => {
    navigate(`/app/questions/${q.id}/edit`);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteQuestion.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  };

  const questions = result?.data ?? [];
  const total = result?.total ?? 0;
  const totalPages = result?.pages ?? 0;
  const bankTotal = isFiltering ? bankCount?.total ?? 0 : total;

  // Deleting the last rows of the last page would otherwise strand the user on an empty page.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) rawSetPage(totalPages);
  }, [totalPages, page, rawSetPage]);

  const allSelected = questions.length > 0 && questions.every((q) => selectedIds.has(q.id));

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(questions.map((q) => q.id)) : new Set());
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    await bulkDeleteQuestions.mutateAsync([...selectedIds]);
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const getSubjectName = (id: string) =>
    (subjects as Subject[]).find((s) => s.id === id)?.name ?? id;

  const typeLabel = (qType: string) => {
    const meta = QUESTION_TYPE_META[qType];
    return meta?.labelKey ? t(meta.labelKey) : qType;
  };

  const subjectOptions: FilterOption[] = (subjects as Subject[]).map((s) => ({ value: s.id, label: s.name }));
  const typeOptions: FilterOption[] = QUESTION_TYPE_ORDER.map((qt) => ({ value: qt, label: typeLabel(qt) }));
  const difficultyOptions: FilterOption[] = DIFFICULTIES.map((d) => ({ value: d, label: t(d) }));

  const previewQuestion = previewId ? questions.find((q) => q.id === previewId) ?? null : null;
  const selecting = canDelete && selectedIds.size > 0;

  const renderBody = () => {
    if (isLoading) return <LoadingRows />;
    if (isError) {
      return (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      );
    }
    if (questions.length === 0 && isFiltering) {
      return (
        <EmptyState
          icon={<Search size={28} />}
          title={t('no_results')}
          description={t('try_adjusting_your_search_or_filters')}
          secondaryAction={{ label: t('clear_filters'), onClick: resetFilters }}
        />
      );
    }
    if (questions.length === 0) {
      return (
        <EmptyState
          icon={<Library size={28} />}
          title={t('qb_empty')}
          action={canCreate ? { label: t('add_question'), onClick: openCreate } : undefined}
        />
      );
    }
    return (
      <div className="relative">
        {/* Selecting rows swaps the column header for the bulk actions (same height, so nothing shifts). */}
        {selecting && (
          <div
            role="toolbar"
            aria-label={t('n_of_m_selected', { selected: selectedIds.size, total: questions.length })}
            className="absolute inset-x-0 top-0 z-10 flex h-10 items-center gap-3 bg-foreground pl-4 pr-2 text-[13px] text-background"
          >
            <Checkbox
              checked={allSelected ? true : 'indeterminate'}
              onCheckedChange={(v) => toggleAll(v === true)}
              aria-label={t('select_all')}
              className="border-background/50"
            />
            <span className="font-semibold tabular-nums">
              {t('n_of_m_selected', { selected: selectedIds.size, total: questions.length })}
            </span>
            <span aria-hidden className="h-[18px] w-px bg-background/25" />
            <button
              type="button"
              onClick={() => setBulkDeleteOpen(true)}
              disabled={bulkDeleteQuestions.isPending}
              className="inline-flex h-[30px] items-center gap-1.5 rounded-md px-2.5 font-medium text-red-300 transition-colors dark:text-red-600 hover:bg-background/10 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              {t('delete')}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="ml-auto inline-flex h-[30px] items-center rounded-md px-2.5 text-background/70 transition-colors hover:bg-background/10 hover:text-background"
            >
              {t('clear_selection')}
            </button>
          </div>
        )}
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {canDelete && (
                <TableHead className="w-10 pr-0">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(v) => toggleAll(v === true)}
                    aria-label={t('select_all')}
                    tabIndex={selecting ? -1 : undefined}
                  />
                </TableHead>
              )}
              <TableHead className="w-12 pr-0">
                <span className="sr-only">{t('type')}</span>
              </TableHead>
              <TableHead>{t('question')}</TableHead>
              <TableHead className="hidden w-44 md:table-cell xl:w-52">{t('subject')}</TableHead>
              <TableHead className="hidden w-40 lg:table-cell">{t('type')}</TableHead>
              <TableHead className="w-28">{t('difficulty')}</TableHead>
              <TableHead className="hidden w-20 text-right xl:table-cell">{t('points')}</TableHead>
              <TableHead className="w-14">
                <span className="sr-only">{t('more_options')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {questions.map((q) => {
              const meta = QUESTION_TYPE_META[q.type] ?? DEFAULT_TYPE_META;
              const TypeIcon = meta.icon;
              const isSelected = selectedIds.has(q.id);
              return (
                <TableRow
                  key={q.id}
                  tabIndex={0}
                  aria-haspopup="dialog"
                  onClick={(event) => openPreview(q.id, event.currentTarget)}
                  onKeyDown={(event) => {
                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      openPreview(q.id, event.currentTarget);
                    }
                  }}
                  className={cn(
                    'h-[52px] cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring [&>td]:py-1.5',
                    (isSelected || previewId === q.id) && 'bg-muted/60 hover:bg-muted/60',
                  )}
                  data-state={isSelected ? 'selected' : undefined}
                >
                  {canDelete && (
                    <TableCell className="pr-0" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(v) => toggleOne(q.id, v === true)}
                        aria-label={t('select')}
                      />
                    </TableCell>
                  )}
                  <TableCell className="pr-0">
                    <span
                      className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-foreground/70"
                      title={typeLabel(q.type)}
                    >
                      <TypeIcon aria-hidden="true" className="h-[15px] w-[15px]" strokeWidth={1.75} />
                      <span className="sr-only lg:hidden">{typeLabel(q.type)}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-[13.5px] text-foreground">
                    <QuestionSummary question={q} />
                  </TableCell>
                  <TableCell className="hidden text-[13px] text-foreground/80 md:table-cell">
                    <p className="line-clamp-2 break-words leading-snug" title={getSubjectName(q.subjectId)}>
                      {getSubjectName(q.subjectId)}
                    </p>
                  </TableCell>
                  <TableCell className="hidden text-[13px] text-foreground/80 lg:table-cell">
                    <p className="line-clamp-2 break-words leading-snug">{typeLabel(q.type)}</p>
                  </TableCell>
                  <TableCell>
                    {q.difficulty ? (
                      <StatusBadge tone={DIFFICULTY_TONE[q.difficulty] ?? 'neutral'} dot={false}>
                        {t(q.difficulty)}
                      </StatusBadge>
                    ) : (
                      <span className="text-muted-foreground/60">—</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden text-right text-[13px] tabular-nums xl:table-cell">{q.points}</TableCell>
                  <TableCell className="py-0 text-right" onClick={(e) => e.stopPropagation()}>
                    {(canEdit || canDelete) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('more_options')}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && (
                            <DropdownMenuItem onSelect={() => openEdit(q)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('edit')}
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => setDeleteTarget(q)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('delete')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2.5">
            {t('question_bank')}
            {bankTotal > 0 && (
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                {formatGroupedNumber(bankTotal)}
              </span>
            )}
          </span>
        }
        actions={
          canCreate && (
            <>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4" strokeWidth={1.75} />
                {t('import')}
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setAiOpen(true)}>
                <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                {t('generate_with_ai')}
              </Button>
              <Button size="sm" className="gap-1.5" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                {t('add_question')}
              </Button>
            </>
          )
        }
      />

      <GenerateWithAIModal
        isOpen={aiOpen}
        onClose={() => setAiOpen(false)}
        subjects={subjects as Subject[]}
        defaultSubjectId={subjectId}
      />

      <ImportQuestionsModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        defaultSubjectId={subjectId}
      />

      <div className="flex flex-wrap items-center gap-2">
        <TableSearch
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('qb_search_placeholder')}
          aria-label={t('qb_search_placeholder')}
        />
        <FilterButton
          label={t('subject')}
          options={subjectOptions}
          value={subjectId}
          onChange={(value) => updateParams({ subjectId: value })}
          loading={subjectsLoading}
          searchPlaceholder={t('search')}
        />
        <FilterButton
          label={t('type')}
          options={typeOptions}
          value={type}
          onChange={(value) => updateParams({ type: value })}
        />
        <FilterButton
          label={t('difficulty')}
          options={difficultyOptions}
          value={difficulty}
          onChange={(value) => updateParams({ difficulty: value })}
        />
        {(hasFilters || searchInput) && <FilterReset onClick={resetFilters} />}
        {!isLoading && !isError && (
          <TableCount className="text-[13px]">
            <span className="font-semibold tabular-nums text-foreground">{formatGroupedNumber(total)}</span>{' '}
            {t('pager_noun_questions')}
          </TableCount>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {/* While the next page loads the previous one stays, dimmed, so nothing jumps. */}
        <div className={cn('transition-opacity', isPlaceholderData && 'opacity-60')} aria-busy={isPlaceholderData || undefined}>
          {renderBody()}
        </div>
        {!isLoading && !isError && total > 0 && (
          <div className="border-t border-border px-4 py-3">
            <TablePager
              page={page}
              pageCount={totalPages || 1}
              pageSize={limit}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              noun={t('pager_noun_questions')}
            />
          </div>
        )}
      </div>

      {/* Drawer is portaled so opening it never compresses the table. */}
      {previewQuestion && (
        <QuestionPreviewPanel
          question={previewQuestion}
          subjectName={getSubjectName(previewQuestion.subjectId)}
          canEdit={canEdit}
          onEdit={() => openEdit(previewQuestion)}
          onClose={() => setPreviewId(null)}
          returnFocusRef={previewTriggerRef}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('qb_delete_question_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('trash.question_delete_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('qb_delete_questions_title', { count: selectedIds.size })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('trash.questions_delete_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Phones render the dedicated mobile card list + filter/preview sheets; the
// switch is a thin wrapper so hook count stays constant across the breakpoint.
export const QuestionBankPage: React.FC = () =>
  useIsMobile() ? <QuestionBankMobile /> : <QuestionBankDesktop />;
